import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {GitCommitIcon} from '@primer/octicons-react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {ReferencedEventNode} from './issue-viewer-page-query-types'
import styles from './ReferencedEvent.module.css'
import {
  ReferencedEventInner,
  type ReferencedEventInnerFragment,
  type ReferencedEventInnerProps,
} from './ReferencedEventInner'
import {Ago} from './row/Ago'
import type {EventActorFragment} from './row/EventActor'
import {TimelineRow} from './row/TimelineRow'
import type {SecondaryTimelineSource} from './secondary-timeline-source'

// RELAY-CLEANUP(timeline pagination): widens nullable fields so still-Relay parent data is assignable.
// Remove when the timeline pagination fragments leave Relay.
export type ReferencedEventFragment = RelayCompat<
  Pick<ReferencedEventNode, 'id' | 'databaseId' | 'willCloseSubject' | 'createdAt'> & {
    readonly subject: {readonly __typename: string}
    readonly actor: EventActorFragment | null
    readonly commit: ReferencedEventInnerFragment | null
  }
>

type ReferencedEventProps = {
  queryRef: ReferencedEventFragment
  rollupGroup?: Record<string, ReferencedEventFragment[]>
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  viewer: string | null
  secondaryTimelineSource?: SecondaryTimelineSource
}

export function ReferencedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  viewer,
  secondaryTimelineSource,
}: ReferencedEventProps): React.ReactElement {
  const {actor, createdAt, commit, databaseId, subject, willCloseSubject} = queryRef

  if (commit === null) {
    return <></>
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup && rollupGroup['ReferencedEvent'] ? rollupGroup['ReferencedEvent'] : [queryRef]
  const numItems = rolledUpGroup.length

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={issueUrl}
      onLinkClick={onLinkClick}
      showAgoTimestamp={false}
      leadingIcon={GitCommitIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.addedCommitsThatReferences(numItems)} `}
        <Ago timestamp={new Date(createdAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        <div className={styles.Box}>
          {rolledUpGroup.map((item, index) => (
            <ReferencedItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${databaseId}_${index}`}
              item={item}
              subjectType={subject.__typename}
              viewerLogin={viewer}
              willCloseSubject={willCloseSubject}
              secondaryTimelineSource={secondaryTimelineSource}
            />
          ))}
        </div>
      </TimelineRow.Main>
    </TimelineRow>
  )
}

function ReferencedItem({
  item,
  ...props
}: {item: ReferencedEventFragment} & Omit<ReferencedEventInnerProps, 'commit' | 'eventId'>) {
  const {commit} = item
  if (!commit) return null

  // Lookup is by event id, so passing the group's head gave every rolled-up row its badge.
  return <ReferencedEventInner commit={commit} eventId={item.id} {...props} />
}
